/** angular */
import { Component, Input, Output, EventEmitter } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatTableDataSource } from '@angular/material/table';
import { FormControl } from '@angular/forms';
/** end angular */

/** interfaces */
import { DsCheckboxRowKeys } from './ds-checkbox-row-keys.interface';
import { DsCheckboxHeaderItem } from './ds-checkbox-header-item.interface';
/** end interfaces */

@Component({
  selector: 'ds-lib-checkbox-table',
  templateUrl: './ds-lib-checkbox-table.component.html',
  styleUrls: ['./ds-lib-checkbox-table.component.scss'],
})
export class DsLibCheckboxTableComponent {
  /**
   * Custom keys for row objects.
   *
   * @param rowKeys DsCheckboxRowKeys
   */
  @Input('rowKeys') set _rowKeys(rowKeys: DsCheckboxRowKeys) {
    this.displayedColumns = this.displayedColumns.filter((displayedColumn) => displayedColumn !== this.rowKeys.name);
    this.rowKeys = this.getRowKeys(rowKeys);
    this.displayedColumns.unshift(this.rowKeys.name);
  }

  /**
   *
   */
  rowKeys: DsCheckboxRowKeys = {
    id: 'id',
    name: 'name',
    subtitle: 'subtitle',
    selected: 'selected',
    checkbox: 'checkbox',
    info: 'info',
  };

  /**
   * Set headers.
   *
   * @param headers DsCheckboxHeaderItem
   */
  @Input() set headers(headers: DsCheckboxHeaderItem[]) {
    this.currentHeaders = JSON.parse(JSON.stringify(headers));
    /**
     * Add header id property for mat-table to prevent bugs when two headers have the same name
     */
    this.currentHeaders.forEach((h) => (h.headerId = h.name + (h.id ? h.id : '')));
    /**
     * Reset displayed columns everytime the headers are changed
     */
    this.displayedColumns = [];
    /**
     * The fixed column name will be rowKeys.name, otherwhise matSort doesn't work right off the bat for the fixed column
     */
    this.displayedColumns.push(this.rowKeys.name);
    this.displayedColumns = this.displayedColumns.concat(this.currentHeaders.map((header) => header.headerId));
  }

  /**
   * Array of headers
   */
  currentHeaders: DsCheckboxHeaderItem[] = [];

  /**
   * Array of column names to display
   */
  displayedColumns: string[] = [];

  /**
   * Custom key for checked property
   */
  selectedKey: 'name' | 'id' = 'id';

  /**
   * Size of a clickable box in PX (the height cannot be inferior to the height of a fixed row height).
   */
  @Input() boxSize = 50;

  /**
   * List of row ids selected with checkboxes
   */
  @Input() selectedRows: number[] = [];

  /**
   * Check if the column is sortable or not.
   */
  @Input() isSortable = true;

  /**
   * Set rows.
   *
   * @param rows the rows
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() set rows(rows: any[]) {
    rows.forEach((row) => {
      if (!row[this.rowKeys.selected]) {
        row[this.rowKeys.selected] = {};
      }
    });
    this.currentRows = rows;
    if (!this.dataSource) {
      this.dataSource = new MatTableDataSource(this.currentRows);
    } else {
      this.dataSource.data = this.currentRows;
    }
  }

  /**
   * Name that will be displayed for the fixed column
   */
  @Input() fixedColumn = '';

  /**
   * Whether the user can select a row/several rows with checkboxes. If false, no checkboxes appear
   */
  @Input() canSelectRow = true;

  /**
   * Set selected Key.
   *
   * @param selectedKey the selected key
   */
  @Input('selectedKey') set _selectedKey(selectedKey: 'name' | 'id') {
    if (!['name', 'id'].includes(selectedKey)) {
      return;
    }
    this.selectedKey = selectedKey;
  }

  /**
   * Custom width for the fixed column
   */
  @Input() fixedColumnWidth: number = undefined;

  /**
   * Show/Hide the search component
   */
  @Input() isSearchDisplayed = true;

  /**
   * The placeholder displayed inside the search bar
   */
  @Input() filterPlaceholder = 'search';

  /**
   * The form control used to search
   */
  private _searchControl: FormControl;

  /**
   * Set searchControl.
   *
   * @param control FormControl
   */
  @Input()
  public set searchControl(control: FormControl) {
    this._searchControl = control;
  }

  /**
   * Get searchControl.
   *
   * @returns FormControl
   */
  public get searchControl(): FormControl {
    return this._searchControl;
  }

  /**
   * Emits when search loses focus
   */
  @Output() searchBlur = new EventEmitter();

  /**
   * Emits clicked row and corresponding header if the column is not disabled
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() boxToggleChange = new EventEmitter<[any, DsCheckboxHeaderItem]>();

  /**
   * Emits when a header is clicked. Returns header object.
   */
  @Output() headerClick = new EventEmitter<DsCheckboxHeaderItem>();

  /**
   * Emits selectedRows array when the selection changes
   */
  @Output() selectedRowsChange = new EventEmitter<number[]>();

  /**
   * Emits an event when the sorting of fixed column is changed
   */
  @Output() sortDirectionChange = new EventEmitter<'asc' | 'desc' | ''>();

  /**
   * Array of displayed rows
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentRows: any[] = [];

  /**
   * Mat table dataSource
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataSource: MatTableDataSource<any> = null;

  /**
   *
   */
  sortDirection: 'asc' | 'desc' | '' = '';

  /**
   * Function called after a click on a box.
   *
   * @param row the toggle box row
   * @param header DsCheckboxHeaderItem
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toggleBox(row: any, header: DsCheckboxHeaderItem) {
    if (header.disabled) {
      return;
    }
    this.boxToggleChange.emit([row, header]);
  }

  /**
   * Emits header clicked.
   *
   * @param header clicked header object
   */
  headerClicked(header: DsCheckboxHeaderItem) {
    this.headerClick.emit(header);
  }

  /**
   * Toggles row selection.
   *
   * @param row Selected row
   * @param event Checkbox change event
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectRowToggle(row: any, event: MatCheckboxChange) {
    switch (event.checked) {
      case true:
        this.selectedRows.push(row[this.rowKeys.id]);
        break;
      case false:
        this.selectedRows = this.selectedRows.filter((selectedRow) => selectedRow !== row[this.rowKeys.id]);
        break;
    }
    this.selectedRowsChange.emit(this.selectedRows);
  }

  /**
   * Emit sort direction Change.
   */
  changeSort() {
    switch (this.sortDirection) {
      case 'asc':
        this.sortDirection = 'desc';
        break;
      case 'desc':
        this.sortDirection = '';
        break;
      case '':
        this.sortDirection = 'asc';
        break;
    }
    this.sortDirectionChange.emit(this.sortDirection);
  }

  /**
   * Returns a rowKeys object containing customKeys data,
   * and also default values if the custom object did not contain all the keys.
   *
   * @param customKeys Custom rowKeys object
   * @returns DsCheckboxRowKeys
   */
  getRowKeys(customKeys: DsCheckboxRowKeys): DsCheckboxRowKeys {
    const rowKeys: DsCheckboxRowKeys = {
      id: 'id',
      name: 'name',
      subtitle: 'subtitle',
      selected: 'selected',
      checkbox: 'checkbox',
      info: 'info',
    };
    for (const key in rowKeys) {
      if (customKeys[key]) {
        rowKeys[key] = customKeys[key];
      }
    }
    return rowKeys;
  }
}
